import { Link } from "react-router-dom";
import { ArrowRight, Check, Home as HomeIcon, Car, Layers, Footprints, Sparkles, Building2 } from "lucide-react";
import { SERVICES, BRAND } from "@/data/site";
import { Reveal, MaskedLine, ChapterLabel } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const ICONS = { "house-washing": HomeIcon, driveways: Car, "decks-fences": Layers, sidewalks: Footprints, patios: Sparkles, commercial: Building2 };

export default function Services() {
  return (
    <main data-testid="services-page">
      <section className="relative pt-40 pb-24 bg-[#060f26] border-b border-cyan-400/10 grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskedLine delay={0.1}>
            <span className="font-mono-brand text-xs sm:text-sm tracking-[0.35em] uppercase text-cyan-400 font-bold">Full Service Catalog</span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-black italic uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl" data-testid="services-title">
            <MaskedLine delay={0.25}>Every Surface.</MaskedLine>
            <MaskedLine delay={0.4} className="text-cyan-400">Restored.</MaskedLine>
          </h1>
          <MaskedLine delay={0.55}>
            <p className="mt-6 max-w-2xl text-slate-300 leading-relaxed">
              From delicate soft-wash house siding to 4000 PSI concrete restoration — calibrated pressure for every material.
            </p>
          </MaskedLine>
        </div>
      </section>

      <Marquee />

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-20">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.id];
            const flip = i % 2 === 1;
            return (
              <Reveal key={s.id}>
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center bg-[#0b1838] border border-cyan-400/15 rounded-3xl p-8 sm:p-12 hover:border-cyan-400/40 transition-colors ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}
                  data-testid={`service-detail-${s.id}`}
                >
                  <div>
                    <ChapterLabel number={String(i + 1).padStart(2, "0")} text={s.tag} />
                    <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-3xl sm:text-5xl">{s.name}</h2>
                    <p className="mt-5 text-slate-300 leading-relaxed">{s.short}</p>
                    <p className="mt-4 text-sm text-slate-400">
                      <span className="font-mono-brand text-xs tracking-[0.25em] uppercase text-cyan-400 font-bold block mb-1">Ideal For</span>
                      {s.idealFor}
                    </p>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="w-14 h-14 rounded-2xl border border-cyan-400/40 bg-cyan-400/10 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <ul className="flex flex-col gap-3">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-center gap-3 text-slate-300 text-sm">
                          <span className="w-5 h-5 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3 text-cyan-400" />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`service-estimate-link-${s.id}`}
                      className="group mt-2 inline-flex items-center gap-2 text-cyan-400 font-bold text-sm uppercase tracking-widest hover:text-cyan-300 transition-colors"
                    >
                      Get This Cleaned <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-24 bg-[#060f26] border-t border-cyan-400/10 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <Reveal>
            <h2 className="font-display font-black italic uppercase tracking-tighter text-4xl sm:text-6xl">
              Not Sure What You Need? <span className="text-cyan-400">Just Ask.</span>
            </h2>
            <p className="mt-5 text-slate-400">Call or text {BRAND.phone} — talk directly with the owner, get a straight answer and a free estimate.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href={`tel:${BRAND.phoneRaw}`} data-testid="services-call-button" className="bg-cyan-400 text-[#030712] font-bold px-8 py-4 rounded-full hover:bg-cyan-300 hover:scale-105 transition-transform">
                Call {BRAND.phone}
              </a>
              <a href={`sms:${BRAND.phoneRaw}`} data-testid="services-text-button" className="border border-cyan-400/50 text-cyan-300 font-bold px-8 py-4 rounded-full hover:bg-cyan-400/10 transition-colors">
                Text Us Instead
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
